/**
 * Staff notification bell (tracker item 62): the bell button with an unread
 * badge in the app header and its dropdown of the newest alerts. The count is
 * polled by AppLayout (useStaffNotificationCount) and passed in; opening the
 * panel loads the 10 newest. Clicking an alert marks it read and opens its
 * record; "Mark all read" only marks what was loaded (`before`), so an alert
 * that arrives while the panel is open stays unread. Escape or a click outside
 * closes the panel and focus returns to the bell. Separate from the customer
 * notifications (tracker items 35, 43) and from the Inbox badge (item 56), which
 * counts the same conversations without being added to this number.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAPIClient, IStaffNotificationsApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import {
  STAFF_ALERT_PREFERENCES_PATH,
  STAFF_NOTIFICATIONS_PATH,
  badgeText,
  notifyStaffNotificationsChanged,
  staffNotificationPath,
  timeAgo,
} from '../utils/staffNotifications';

const PANEL_LIMIT = 10;

export function BellIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 01-3.4 0" />
    </svg>
  );
}

export interface StaffNotificationBellProps {
  unread: number;
  capped: boolean;
  /** Reload the count (after a read-state change) */
  onChanged: () => void;
  /** Which side the panel opens to: from the top bar (right edge) or the sidebar (left edge) */
  placement: 'top-bar' | 'sidebar';
}

export function StaffNotificationBell({ unread, capped, onChanged, placement }: StaffNotificationBellProps) {
  const api = useMemo<IStaffNotificationsApi>(() => createAPIClient(), []);
  const navigate = useNavigate();
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<types.StaffNotification[] | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const page = await api.listStaffNotifications({ limit: PANEL_LIMIT });
      setItems(page.data);
      setLoadedAt(new Date().toISOString());
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load notifications'));
    }
  }, [api]);

  // Opening the panel reloads the list and the count
  useEffect(() => {
    if (!open) return;
    void load();
    onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, load]);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) close(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close]);

  const openItem = async (item: types.StaffNotification) => {
    const path = staffNotificationPath(item.link);
    if (!item.readAt) {
      try {
        await api.markStaffNotificationRead(item.id);
      } catch {
        // Opening the record matters more than the read mark
      }
      notifyStaffNotificationsChanged();
    }
    close(false);
    if (path) navigate(path);
  };

  const markAll = async () => {
    try {
      await api.markAllStaffNotificationsRead(loadedAt ? { before: loadedAt } : {});
      notifyStaffNotificationsChanged();
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not mark notifications read'));
    }
  };

  const label = unread > 0 ? `Notifications, ${badgeText(unread, capped)} unread` : 'Notifications';
  // From the top bar on a phone the panel spans the screen width (the bell is not at the edge); otherwise it hangs under the bell
  const position =
    placement === 'sidebar'
      ? 'absolute left-0 top-full mt-2 w-[22rem] max-w-[calc(100vw-1.5rem)]'
      : 'fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[22rem]';

  return (
    <div ref={containerRef} className="relative" data-testid={`staff-bell-${placement}`}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center min-h-[44px] min-w-[44px] lg:min-h-[36px] lg:min-w-[36px] rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
      >
        <BellIcon className="h-6 w-6" />
        {unread > 0 && (
          <span
            data-testid="staff-bell-badge"
            className="absolute right-0.5 top-0.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-red-600 px-1 text-[11px] font-semibold leading-5 text-white"
          >
            {badgeText(unread, capped)}
          </span>
        )}
      </button>
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Notifications"
          className={`${position} z-50 rounded-md border border-gray-200 bg-white shadow-lg`}
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
            <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
            <button
              type="button"
              onClick={() => void markAll()}
              disabled={!items || items.every((i) => i.readAt)}
              className="min-h-[44px] sm:min-h-0 text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-400"
            >
              Mark all read
            </button>
          </div>
          {error && <p role="alert" className="px-3 py-2 text-sm text-red-700">{error}</p>}
          {!items && !error && <p className="px-3 py-4 text-sm text-gray-500">Loading...</p>}
          {items && items.length === 0 && <p className="px-3 py-4 text-sm text-gray-500">You are all caught up.</p>}
          {items && items.length > 0 && (
            <ul className="max-h-96 divide-y divide-gray-100 overflow-y-auto">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => void openItem(item)}
                    data-unread={item.readAt ? 'false' : 'true'}
                    className={`block w-full min-h-[44px] px-3 py-2 text-left hover:bg-gray-50 ${item.readAt ? '' : 'bg-blue-50/60'}`}
                  >
                    <span className="flex items-start gap-2">
                      {!item.readAt && <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600" />}
                      <span className="min-w-0 flex-1">
                        <span className={`block break-words text-sm ${item.readAt ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>{item.title}</span>
                        {item.body && <span className="block break-words text-xs text-gray-600">{item.body}</span>}
                        <span className="block text-xs text-gray-500">{timeAgo(item.createdAt)}</span>
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-3 py-2 text-xs font-semibold">
            <Link to={STAFF_NOTIFICATIONS_PATH} onClick={() => close(false)} className="flex min-h-[44px] items-center text-blue-600 hover:text-blue-700 sm:min-h-0">
              View all
            </Link>
            <Link to={STAFF_ALERT_PREFERENCES_PATH} onClick={() => close(false)} className="flex min-h-[44px] items-center text-blue-600 hover:text-blue-700 sm:min-h-0">
              Alert settings
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
